import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserInfo } from '../../common/model/dto/user-info';
import { useAppSelector } from '../../redux/hooks';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvNoContent from '../UI/molecules/OvNoContent';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import { AssignPatientToClinicLocationRequest } from '../../common/model/dto/assign-user-to-clinic-location-request';
import OvButton from '../UI/atoms/OvButton';
import Variables from '../../design-system/variables';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import OvAssignPatientToClinicLocationDialog from '../UI/molecules/OvAssignPatientToClinicLocationDialog';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { ClinicLocationService } from '../../services/clinic-location.service';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OvClinicLocationUserList from '../UI/organisms/OvClinicLocationUserList';
import { CreateDemoAccountRequest } from '../../common/model/dto/create-demo-account-request';
import OvCreateDemoAccountDialog from '../UI/molecules/OvCreateDemoAccountDialog';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import { ClinicLocationUtils } from '../../common/utils/services/clinic-location-utils';
import { useQueryParam, withDefault, StringParam } from 'use-query-params';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import { AxiosError } from 'axios';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { useClinicLocationPatients } from '../../hooks/use-clinic-location-patients';
import { useClinicLocationPatientMutations } from '../../hooks/use-clinic-location-patient-mutations';
import OvProviderPatientSearchFilter from '../UI/organisms/OvProviderPatientSearchFilter';
import { FilterModel } from '../../common/model/ui/filter.model';
import { OvSortingButtonModel } from '../UI/molecules/OvSortingButtonGroup';
import { OrderByDirectionType } from '../../common/model/type/order-by-direction.type';
import OvSearchInput from '../UI/atoms/OvSearchInput';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';

const LIMIT = ClinicLocationService.CLINIC_LOCATION_USER_LIST_LIMIT;
const DEFAULT_ORDER_BY = 'id';
const DEFAULT_ORDER_DIR = 'asc';

const FilterablePatientList: FC = () => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicPatientOperations = useAuthorized(Resource.ClinicPatient);
  const demoAccountOperations = useAuthorized(Resource.DemoAccount);

  const clinicPatientCreateAllowed =
    clinicPatientOperations.create || applicationOperations.supervise;
  const clinicPatientUpdateAllowed =
    clinicPatientOperations.update || applicationOperations.supervise;
  const clinicPatientDeleteAllowed =
    clinicPatientOperations.delete || applicationOperations.supervise;
  const demoAccountCreateAllowed =
    demoAccountOperations.create || applicationOperations.supervise;

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCreateDemoAccountDialogOpen, setIsCreateaDemoAccountDialogOpen] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const selectedClinicLocation: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const [searchText, setSearchText] = useQueryParam(
    'search_text',
    withDefault(StringParam, '')
  );
  const [knownReproductiveDisorder, setKnownReproductiveDisorder] =
    useQueryParam('known_reproductive_disorder', withDefault(StringParam, ''));
  const [shouldHideNotRegisteredUsers, setShouldHideNotRegisteredUsers] =
    useQueryParam(
      'should_hide_not_registered_users',
      withDefault(StringParam, '')
    );
  const [isDemoAccount, setIsDemoAccount] = useQueryParam(
    'is_demo_account',
    withDefault(StringParam, '')
  );
  const [age, setAge] = useQueryParam('age', withDefault(StringParam, ''));
  const [averageCycleLength, setAverageCycleLength] = useQueryParam(
    'length_of_average_monthly_cycle',
    withDefault(StringParam, '')
  );

  // Initialise the default sort once (mirrors the legacy "load with id/asc" behaviour).
  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy(DEFAULT_ORDER_BY);
      setOrderDir(DEFAULT_ORDER_DIR);
    }
  }, [orderBy, orderDir, setOffset, setOrderBy, setOrderDir]);

  const query = queryString.stringify(
    {
      limit: LIMIT,
      order_by: isEmpty(orderBy) ? undefined : orderBy,
      order_dir: isEmpty(orderDir) ? undefined : orderDir,
      offset: offset ?? 0,
      search_text: isEmpty(searchText)
        ? undefined
        : searchText.toUpperCase().trim().split(/\s+/).join(','),
      known_reproductive_disorder: isEmpty(knownReproductiveDisorder)
        ? undefined
        : knownReproductiveDisorder,
      should_hide_not_registered_users: isEmpty(shouldHideNotRegisteredUsers)
        ? undefined
        : shouldHideNotRegisteredUsers,
      is_demo_account: isEmpty(isDemoAccount) ? undefined : isDemoAccount,
      age: isEmpty(age) ? undefined : age,
      length_of_average_monthly_cycle: isEmpty(averageCycleLength)
        ? undefined
        : averageCycleLength,
    },
    { skipNull: true }
  );

  const {
    data: paginatedPatients,
    isPending,
    isFetching,
    error: getPatientsError,
  } = useClinicLocationPatients(
    selectedClinicLocation?.id,
    query,
    !!orderBy && !!orderDir
  );

  const { assignPatientToClinicLocation, createDemoAccount } =
    useClinicLocationPatientMutations(selectedClinicLocation?.id);

  const patientList: UserInfo[] = paginatedPatients?.docs ?? [];
  const total = paginatedPatients?.total ?? 0;

  const isLoading =
    isPending ||
    isFetching ||
    assignPatientToClinicLocation.isPending ||
    createDemoAccount.isPending;

  const sortingOptions: OvSortingButtonModel[] = [
    {
      value: 'id',
      label: 'common.userFields.userId',
      direction: (orderBy === 'id' ? orderDir : '') as OrderByDirectionType,
      multiDir: false,
    },
    {
      value: 'latest_scan_date',
      label: 'common.userFields.latestScan',
      direction: (orderBy === 'latest_scan_date'
        ? orderDir
        : '') as OrderByDirectionType,
      multiDir: false,
    },
    {
      value: 'first_name',
      label: 'common.userFields.firstName',
      direction: (orderBy === 'first_name'
        ? orderDir
        : '') as OrderByDirectionType,
      multiDir: true,
    },
    {
      value: 'last_name',
      label: 'common.userFields.lastName',
      direction: (orderBy === 'last_name'
        ? orderDir
        : '') as OrderByDirectionType,
      multiDir: true,
    },
  ];

  const onHandleSorting = (sortingValue: string) => {
    const option = sortingOptions.find((opt) => opt.value === sortingValue);
    const previousDir = sortingValue === orderBy ? orderDir : '';

    let newDir: OrderByDirectionType;
    if (option?.multiDir) {
      newDir = previousDir === 'asc' ? 'desc' : 'asc';
    } else {
      newDir = sortingValue === 'latest_scan_date' ? 'desc' : 'asc';
    }

    setOffset(0);
    setOrderBy(sortingValue);
    setOrderDir(newDir);
  };

  const onHandleFilterValueChange = (filter: FilterModel) => {
    setOffset(0);

    switch (filter.fieldName) {
      case 'known_reproductive_disorder':
        setKnownReproductiveDisorder(filter.value || undefined);
        break;
      case 'should_hide_not_registered_users':
        setShouldHideNotRegisteredUsers(
          filter.value === '' ? undefined : String(filter.value)
        );
        break;
      case 'is_demo_account':
        setIsDemoAccount(
          filter.value === '' ? undefined : String(filter.value)
        );
        break;
      case 'age':
        setAge(
          Array.isArray(filter.value) ? filter.value.join(',') : undefined
        );
        break;
      case 'length_of_average_monthly_cycle':
        setAverageCycleLength(
          Array.isArray(filter.value) ? filter.value.join(',') : undefined
        );
        break;
      default:
        break;
    }
  };

  const removeFilters = () => {
    setOffset(0);
    setKnownReproductiveDisorder(undefined);
    setShouldHideNotRegisteredUsers(undefined);
    setIsDemoAccount(undefined);
    setAge(undefined);
    setAverageCycleLength(undefined);
  };

  const onCreateDemoAccount = async (
    createDemoAccountRequest: CreateDemoAccountRequest
  ) => {
    setIsCreateaDemoAccountDialogOpen(false);
    const res = await createDemoAccount.mutateAsync(createDemoAccountRequest);
    navigate(`/patients/${res.user.id}`);
  };

  const goToPage = (_event: any, value: number) => {
    setOffset((value - 1) * LIMIT);
  };

  const onSearchChange = (value: string) => {
    setOffset(0);
    setSearchText(value || null);
  };

  const handleOnSave = async (
    request: AssignPatientToClinicLocationRequest
  ) => {
    setIsDialogOpen(false);
    setErrorMessage('');

    try {
      await assignPatientToClinicLocation.mutateAsync(request);
    } catch (error) {
      const responseData = (error as AxiosError)?.response?.data as any;
      setErrorMessage(
        ClinicLocationUtils.isDuplicateKeyError(responseData?.message)
          ? t('clinicLocationDetails.errors.patientAlreadyInvited')
          : (error as AxiosError)?.message
      );
    }
  };

  return (
    <Container>
      <CenterPane>
        <ButtonWrapper>
          {clinicPatientCreateAllowed && (
            <StyledOvButton onClick={() => setIsDialogOpen(true)}>
              <AddCircleIcon style={{ marginRight: '5' }} />
              {t('clinicUsers.actions.addNewPatient')}
            </StyledOvButton>
          )}

          {demoAccountCreateAllowed && (
            <StyledOvButton
              onClick={() => setIsCreateaDemoAccountDialogOpen(true)}
            >
              <PersonAddAltIcon style={{ marginRight: '5' }} />
              {t('common.actions.createDemoAccount')}
            </StyledOvButton>
          )}
        </ButtonWrapper>
        <UserActions>
          <SearchArea>
            <StyledOvSearchInput
              placeholder={t('common.actions.search')}
              initialValue={searchText ?? ''}
              onSearchChange={onSearchChange}
            />
          </SearchArea>
          <OvPaginationBar
            total={total}
            limit={LIMIT}
            offset={offset}
            onChange={goToPage}
          />
        </UserActions>
        {getPatientsError && (
          <OvErrorMessage message={getPatientsError.message} />
        )}
        {errorMessage ? (
          <StyledErrorMessage>
            <StyledErrorOutlineIcon />
            {errorMessage}
          </StyledErrorMessage>
        ) : (
          ''
        )}

        {patientList?.length ? (
          <OvClinicLocationUserList
            users={patientList}
            seeDetails={true}
            seeDetailsCtaLabel={t('common.actions.seeResults')}
            clinicLocationId={selectedClinicLocation?.id}
            clinicId={selectedClinicLocation?.clinic?.id}
            showLatestScanDate={true}
            editable={clinicPatientUpdateAllowed}
            deletable={clinicPatientDeleteAllowed}
            tableHeaders={ClinicLocationUtils.getClinicLocationPatientListTableHeaders()}
          />
        ) : (
          ''
        )}

        {!patientList?.length && !isLoading ? (
          <OvNoContent>
            {t('common.userSearchFilters.emptyUserList')}
          </OvNoContent>
        ) : null}
      </CenterPane>

      <OvProviderPatientSearchFilter
        sortingOptions={sortingOptions}
        onHandleSorting={onHandleSorting}
        onHandleFilterValueChange={onHandleFilterValueChange}
        removeFilters={removeFilters}
        filterValues={{
          knownReproductiveDisorder,
          shouldHideNotRegisteredUsers,
          isDemoAccount,
          age,
          averageCycleLength,
        }}
      />

      <OvAssignPatientToClinicLocationDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
        title={t('clinicLocationDialogs.assignPatientTitle')}
      />

      <OvCreateDemoAccountDialog
        isOpen={isCreateDemoAccountDialogOpen}
        onCancel={() => setIsCreateaDemoAccountDialogOpen(false)}
        onSave={onCreateDemoAccount}
        title={t('common.actions.createDemoAccount')}
        enableCreatePassword={false}
      />
      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Container>
  );
};

export default FilterablePatientList;

const Container = styled.section`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SearchArea = styled.div`
  min-width: 13rem;
  flex-grow: 1;

  @media (min-width: ${breakpoints.md}) {
    min-width: auto;
  }

  & input {
    padding: 0.5rem !important;
    background: ${Colours.WHITE};
  }
`;

const StyledOvSearchInput = styled(OvSearchInput)`
  max-width: 22.5rem;
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
  min-width: 13rem;
  display: flex;
  flex-wrap: 'wrap';
  justify-content: flex-end;
  @media (min-width: ${breakpoints.md}) {
    min-width: auto;
  }
  margin-bottom: 10px;
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    padding: 0.125rem 0.7rem 0.125rem 0.35rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledErrorMessage = styled.p`
  color: ${Colours.OV_RED};
  margin: 10px 0 24px 0;
  display: flex;
  align-items: center;
`;

const StyledErrorOutlineIcon = styled(ErrorOutlineIcon)`
  margin-right: 0.5rem;
`;
